<template>
  <span class="contact-summary-btn-wrap">
    <button
      type="button"
      class="contact-summary-icon-btn"
      :disabled="loading"
      v-tooltip="tooltip"
      :title="tooltip"
      :aria-label="tooltip"
      @click.stop="onGenerateSummary"
    >
      <span v-if="loading" class="contact-summary-spinner" aria-hidden="true" />
      <!-- Sync/refresh icon when summary exists -->
      <svg
        v-else-if="hasExistingSummary"
        class="contact-summary-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M21 2v6h-6" />
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
        <path d="M3 22v-6h6" />
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      </svg>
      <!-- AI summary sparkles icon when no summary yet -->
      <svg
        v-else
        class="contact-summary-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
        <path d="M20 3v4" />
        <path d="M22 5h-4" />
        <path d="M4 17v2" />
        <path d="M6 19H4" />
      </svg>
    </button>
  </span>
</template>

<script>
import { generateContactSummary } from 'dashboard/api/contactSummary';

export default {
  name: 'ContactSummaryButton',
  props: {
    accountId: { type: [Number, String], required: true },
    contactId: { type: [Number, String], required: true },
    /** When true, shows Sync button and icon; when false, shows Generate summary */
    hasExistingSummary: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: null,
    };
  },
  computed: {
    tooltip() {
      if (this.hasExistingSummary) {
        return this.$t('CONTACT_SUMMARY.SYNC') || 'Sync summary';
      }
      return this.$t('CONTACT_SUMMARY.GENERATE') || 'Generate summary';
    },
  },
  methods: {
    async onGenerateSummary() {
      this.loading = true;
      this.error = null;
      try {
        const data = await generateContactSummary(this.accountId, this.contactId);
        const summary =
          typeof data === 'string'
            ? data
            : data.summary ?? data.text ?? data.content ?? (data.contact_summary_metadata && (data.contact_summary_metadata.summary ?? data.contact_summary_metadata['summary']));
        const noConversationMessage = 'No previous conversation found.';
        const isNoConversation = (summary || '').trim() === noConversationMessage;
        const metadata = isNoConversation
          ? null
          : (data.contact_summary_metadata || (data.summary != null ? { summary: data.summary, generated_at: new Date().toISOString() } : null));
        this.$emit('summary', { summary: summary || '', contact_summary_metadata: metadata });
      } catch (e) {
        this.$emit('error', 'Something went wrong. Please try again later.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.contact-summary-btn-wrap {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 0.25rem;
}
.contact-summary-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: var(--color-woot, #1f93ff);
  cursor: pointer;
  border-radius: 4px;
}
.contact-summary-icon-btn:hover:not(:disabled) {
  background: var(--color-background-light, rgba(0, 0, 0, 0.06));
}
.contact-summary-icon-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.contact-summary-icon {
  display: block;
  flex-shrink: 0;
}
.contact-summary-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: contact-summary-spin 0.6s linear infinite;
}
@keyframes contact-summary-spin {
  to { transform: rotate(360deg); }
}
</style>
